import { Component } from 'react';
import Changelog from '../changelog';
import Styled from './ChangelogGuide.style';

// Sample entries exercising every supported feature: all production states, all
// Keep-a-Changelog categories, and all change-item types. This data lives only in
// the guide — it is NOT a changelog entry and never appears in the real tab.
const SAMPLE_ENTRIES = [
    {
        staging: '2026-06-22',
        production: { expected: '2026-06-29' },
        updates: [
            {
                title: 'An update title (e.g. the module or feature name)',
                description: 'An optional one-line summary shown beneath the title, with `code` and [links](/sales).',
                update: [
                    '**Jun 25, 2026:** An optional later note added to an already-published update.'
                ],
                action: [
                    'An optional "What this means for you" note — what (if anything) consumers must do.'
                ],
                changes: {
                    added: [
                        'A plain line — supports inline [links](/sales), **bold**, and `code`.',
                        { list: [
                            'An opt-in bullet point (use only when content is genuinely a list).',
                            'Another point, with `code` and a [link](/lists/read).'
                        ]},
                        { group: { label: 'Request', items: [
                            'A labeled sub-section — a heading above its own bullets.',
                            '**Required** when creating or updating a [contact](/contact).'
                        ]}},
                        { group: { label: 'Response', items: [
                            'Returned in [Contact](/contact) and [Sales](/sales) responses.'
                        ]}}
                    ],
                    security: [
                        'A security note, e.g. patched a token-scope leak affecting `partner` clients.'
                    ]
                }
            },
            {
                title: 'A second update under the same date',
                changes: {
                    fixed: [
                        'Multiple updates render beneath one shared date header, separated by a rule.'
                    ]
                }
            }
        ]
    },
    {
        staging: '2026-06-21',
        production: { expected: '2026-06-28' },
        updates: [
            {
                breaking: {
                    removed: [
                        'Removed the deprecated `/v1/lists` endpoint — use [Lists](/lists/read) instead.'
                    ]
                },
                changes: {
                    changed: [
                        'A normal change line referencing [Sales](/sales).',
                        { diff: { field: 'total_amount', from: 'number', to: 'string' } },
                        { table: {
                            headers: ['Field', 'Before', 'After'],
                            rows: [
                                ['status', 'int', 'enum'],
                                ['issued_at', 'datetime', 'date']
                            ]
                        }}
                    ],
                    deprecated: [
                        'The `legacy_total` field is deprecated; use `grand_total` instead.'
                    ],
                    removed: [
                        'Removed the deprecated `/v1/lists` endpoint.'
                    ]
                }
            }
        ]
    },
    {
        staging: '2026-06-19',
        production: '2026-06-26',
        updates: [
            {
                changes: {
                    added: [
                        'A release that is already live on production (confirmed date).'
                    ],
                    fixed: [
                        'Fixed pagination on [Contacts](/contact) returning duplicate rows.'
                    ]
                }
            }
        ]
    }
];

const ENTRY_SNIPPET = `// src/data/changelog/2026-06-21.json  (one file per release; name it by staging date)
{
  "$schema": "../changelog.schema.json",
  "staging": "2026-06-21",
  "production": "2026-06-28",
  "updates": [             // one object per module/topic shipped on this date
    {
      "title": "Contact Code",       // optional subtitle for this update
      "description": "...",          // optional one-line summary
      "action": [ "..." ],           // optional "What this means for you" notes
      "breaking": {                  // optional; omit when nothing breaks. Shows a ⚠ Breaking badge + section.
        "changed": [ "..." ]
      },
      "changes": {                   // non-breaking changes
        "added":   [ "..." ],
        "changed": [ "..." ]
        // categories: added, changed, deprecated, removed, fixed, security
      }
    }
  ]
}`;

const PRODUCTION_SNIPPET = `"production": "2026-06-28"                  // live on this date
"production": { "expected": "2026-06-28" }  // not live yet -> "Expected 28 June 2026"
// production always carries a date — either confirmed or { "expected": ... }`;

const ITEMS_SNIPPET = `"Plain line with a [link](/sales), **bold**, and \`code\`."   // normal line (not a bullet)
{ "list": ["point one", "point two"] }                       // opt-in indented bullets
{ "group": { "label": "Request",                             // labeled sub-section (heading + bullets)
             "items": ["point one", "point two"] } }
{ "diff": { "field": "total_amount", "from": "number", "to": "string" } }   // field old -> new
{ "table": { "headers": ["Field","Before","After"],          // comparison table
             "rows": [["status","int","enum"]] } }`;

class ChangelogGuide extends Component {
    render() {
        return (
            <Styled>
                <div className="pg-banner">
                    <p className="pg-kicker">Internal reference · local only</p>
                    <h1 className="pg-title">Changelog authoring guide</h1>
                    <p className="pg-intro">
                        A live demo of every field and change-item type the changelog supports.
                        This page is not part of the public changelog — it is reachable only at <code>/changelog-guide</code>.
                    </p>
                </div>

                <Changelog title="Sample changelog" entries={SAMPLE_ENTRIES} />

                <div className="pg-ref">
                    <h2>Authoring reference</h2>
                    <p className="pg-reflead">
                        Each release is one file at <code>src/data/changelog/&lt;staging-date&gt;.json</code>,
                        validated against <code>changelog.schema.json</code>. Files are auto-discovered — just add one.
                    </p>
                    <pre>{ENTRY_SNIPPET}</pre>

                    <h3>Production states</h3>
                    <pre>{PRODUCTION_SNIPPET}</pre>

                    <h3>Change item types</h3>
                    <pre>{ITEMS_SNIPPET}</pre>
                </div>
            </Styled>
        );
    }
}

export default ChangelogGuide;
